import { PriceItemElement } from './PriceItemElement';
// import priceData from '../../locales/ru/price.json';
import ContactModal from '../ContactModal/ContactModal';
import { getUserLang } from '../langUtils';
import { PriceData } from '../../types/types';

// Абстрактный базовый класс для всех версий
abstract class PriceBase {
  menuItems: HTMLElement[];

  priceItems: Map<number, PriceItemElement>;

  currentItem: number = -1;

  cssClass: string = '';

  constructor() {
    this.menuItems = Array.from(document.querySelectorAll('.price__menu-item-btn')) as HTMLElement[];
    this.priceItems = new Map();
  }

  init = () => {
    this.bindEvents();
  };

  bindEvents = () => {
    this.menuItems.forEach((item, index) => {
      item.addEventListener('click', () => this.openItem(index));
    });
  };

  // Абстрактный метод для изменения активного элемента
  protected abstract openItem(itemId: number): void;

  // Абстрактные методы, которые должны быть реализованы в дочерних классах
  // protected abstract hideCurrentItem(item: PriceItemElement): void;
  // protected abstract showExistingItem(item: PriceItemElement, itemId: number): void;
  protected abstract createPriceItem(itemId: number): void;
}

// Класс для десктопной версии
class PriceDesktop extends PriceBase {
  private priceContainer: HTMLElement;

  private priceData: PriceData;

  constructor(priceContainer: HTMLElement, priceData: PriceData) {
    super();
    this.priceContainer = priceContainer;
    this.priceData = priceData;
    this.cssClass = 'price__item';
    this.currentItem = 0;
    this.init();
    // Показываем первый элемент при инициализации
    this.openItem(0);
  }

  openItem = (itemId: number) => {
    if (this.currentItem === itemId && this.priceItems.has(itemId)) return;
    // Скрываем текущий элемент
    if (this.priceItems.has(this.currentItem)) {
      const currentPriceItem = this.priceItems.get(this.currentItem);
      if (currentPriceItem) {
        this.hideCurrentItem(currentPriceItem);
      }
    }

    // Убираем активный класс с предыдущего элемента меню
    this.menuItems[this.currentItem].classList.remove('active');

    // Добавляем активный класс новому элементу меню
    this.menuItems[itemId].classList.add('active');

    // Если элемент уже создан, показываем его
    if (this.priceItems.has(itemId)) {
      const priceItem = this.priceItems.get(itemId);
      if (priceItem) {
        this.showExistingItem(priceItem);
      }
    } else {
      // Если элемент еще не создан, создаем его
      this.createPriceItem(itemId);
    }

    this.currentItem = itemId;
  };

  // Реализация специфичной для десктопа логики скрытия элемента
  protected hideCurrentItem(item: PriceItemElement): void {
    item.node.classList.add('leaving');
    item.node.classList.remove('active');

    // Удаляем элемент после завершения анимации исчезновения
    setTimeout(() => {
      item.delete();
      item.node.classList.remove('leaving');
    }, 200);
  }

  // Реализация специфичной для десктопа логики показа элемента
  protected showExistingItem(item: PriceItemElement): void {
    this.priceContainer.append(item.node);
    // Запускаем анимацию в следующем кадре
    requestAnimationFrame(() => {
      item.node.classList.add('active');
    });
  }

  // Реализация специфичной для десктопа логики создания элемента
  protected createPriceItem(itemId: number): void {
    const data = this.priceData.categories[itemId];
    if (!data) return;

    const priceItem = new PriceItemElement(
      {
        cssClass: this.cssClass,
        soon: this.priceData.soon,
        itemPriceFrom: this.priceData.itemPriceFrom,
        itemPriceTo: this.priceData.itemPriceTo,
        closeBtnText: this.priceData.closeBtnText,
        signUpBtnText: this.priceData.signUpBtnText,
        categoryTitle: data.categoryTitle,
        items: data.items,
      },
      false,
    );

    // Добавляем элемент в DOM
    this.priceContainer.append(priceItem.node);

    // Запускаем анимацию в следующем кадре
    requestAnimationFrame(() => {
      priceItem.node.classList.add('active');
    });

    // Сохраняем элемент в Map
    this.priceItems.set(itemId, priceItem);
  }
}

// Класс для мобильной версии
class PriceMobile extends PriceBase {
  contactModal: ContactModal;

  private priceData: PriceData;

  constructor(contactModal: ContactModal, priceData: PriceData) {
    super();
    this.contactModal = contactModal;
    this.cssClass = 'price__menu-content';
    this.priceData = priceData;
    this.init();
  }

  scrollToItem = (itemId: number) => {
    this.menuItems[itemId].scrollIntoView({ behavior: 'instant', block: 'start' });
  };

  openItem = (itemId: number) => {
    this.menuItems[itemId].classList.toggle('active');
    if (this.priceItems.has(itemId)) {
      const currentPriceItem = this.priceItems.get(itemId);
      if (currentPriceItem) {
        currentPriceItem.node.parentElement?.classList.toggle('active');
        currentPriceItem.node.classList.toggle('active');
      }
    } else {
      this.createPriceItem(itemId);
    }
  };

  protected createPriceItem(itemId: number): void {
    const data = this.priceData.categories[itemId];
    if (!data) return;

    const priceItem = new PriceItemElement(
      {
        cssClass: this.cssClass,
        soon: this.priceData.soon,
        closeBtnText: this.priceData.closeBtnText,
        signUpBtnText: this.priceData.signUpBtnText,
        itemPriceFrom: this.priceData.itemPriceFrom,
        itemPriceTo: this.priceData.itemPriceTo,
        categoryTitle: data.categoryTitle,
        items: data.items,
      },
      true,
    );

    this.contactModal.bindOnBtn(priceItem.node.querySelector('.btn_contact') as HTMLButtonElement);
    priceItem.node.querySelector('.price__item-close-btn')?.addEventListener('click', () => {
      this.openItem(itemId);
      // Скроллим к пункту меню при закрытии
      this.scrollToItem(itemId);
    });

    // Добавляем элемент в DOM
    this.menuItems[itemId].nextElementSibling?.append(priceItem.node);

    // Запускаем анимацию в следующем кадре
    requestAnimationFrame(() => {
      priceItem.node.classList.add('active');
      priceItem.node.parentElement?.classList.add('active');
    });

    this.priceItems.set(itemId, priceItem);
  }
}

// Класс-обертка, который будет использоваться в index.ts
export default class Price {
  private priceInstance: PriceBase | null = null;

  constructor(contactModal: ContactModal) {
    // Определяем тип устройства и создаем соответствующий экземпляр
    const desktopPriceContainer = document.querySelector('.price__item-container') as HTMLElement;
    const isDesktop = window.matchMedia('(min-width: 1440px)').matches;
    const lang = getUserLang();
    this.loadPriceData(lang).then((data) => {
      this.priceInstance = isDesktop
        ? new PriceDesktop(desktopPriceContainer, data)
        : new PriceMobile(contactModal, data);
    });
  }

  loadPriceData = async (lang: string) => {
    const response = await fetch(`/${lang}/price.json`);
    const data = await response.json();
    return data;
  };
}
